import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useSelectableHomeOrganizations } from './use-selectable-home-organizations';
import { Organization } from '@upright/common/iam/organizations/organization-types';
import { useMyOrganizationsQuery } from '@upright/ui/api/iam/organizations/hooks';
import { useIsTeam } from '@upright/ui/common/generic/hooks';
import { organizationIdFromPath, pathOrganizationToPersist } from './organization-id-from-path';
import { resolveSelectedOrganization } from './resolve-selected-organization';

type SelectedOrganizationContextValue = {
  selectedOrganization: Organization | null | undefined;
  /**
   * Takes an organisation, never `null`. "No organisation" is not a state a viewer can choose:
   * the selector's "None" row is gone and every org-scoped view on this page needs one to render
   * (PLT-6696). `selectedOrganization` is still nullable because it is `null` until the
   * organisations query resolves.
   */
  setSelectedOrganization: (org: Organization) => void;
};

/**
 * Query param the selected organization is encoded into, so the selection is
 * reload-safe and bookmarkable (PLT-4990). The matching section nav links use
 * {@link LinkWithSearch}, which preserves the search string when switching
 * sections, so the selection follows the user across home-view routes.
 */
export const ORGANIZATION_ID_SEARCH_PARAM = 'organizationId';

const ctx = createContext<SelectedOrganizationContextValue | null>(null);

/**
 * Hoisted to {@link HomeViewFrame} so the sidebar org selector and the
 * organization-scoped views (investor home, custom companies) share state.
 *
 * The selection is backed by the {@link ORGANIZATION_ID_SEARCH_PARAM} query
 * param rather than component state, so reloading or bookmarking a home-view
 * page preserves the chosen org instead of resetting to the default (PLT-4990).
 */
export function useBuildSelectedOrganization(): SelectedOrganizationContextValue {
  const isTeam = useIsTeam();
  const getOrganizationsQuery = useMyOrganizationsQuery();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();

  const filteredOrgs = useSelectableHomeOrganizations();

  const teamDefaultOrganizationId = getOrganizationsQuery.data?.teamDefaultOrganizationId;
  const orgIdParam = searchParams.get(ORGANIZATION_ID_SEARCH_PARAM);
  const orgIdFromPath = organizationIdFromPath(pathname);

  const selectedOrganization = useMemo<Organization | null>(
    () =>
      resolveSelectedOrganization({
        homeViewOrganizations: filteredOrgs,
        organizationIdParam: orgIdParam,
        pathOrganizationId: orgIdFromPath,
        isTeam,
        teamDefaultOrganizationId,
      }),
    [filteredOrgs, orgIdParam, orgIdFromPath, isTeam, teamDefaultOrganizationId],
  );

  const orgIdToPersist = pathOrganizationToPersist({
    organizationIdParam: orgIdParam,
    pathOrganizationId: orgIdFromPath,
    selectableOrganizationIds: filteredOrgs?.map(org => org.id) ?? [],
  });

  // Write an org adopted from the path into the query param, so the selection
  // survives leaving the wizard. Runs at most once per visit: once the param is
  // set, there is nothing left to persist.
  //
  // Running once also keeps this out of the way of the wizard's own path
  // rewrites. `setSearchParams` resolves `?param` against the pathname captured
  // in the render that created it, so a write racing a pathname change would
  // rewind the path. It cannot race in practice: the param is written as soon as
  // the organizations query resolves, long before a company id can be assigned,
  // and on a resume URL the wizard rewrites to the path it is already on.
  useEffect(() => {
    if (orgIdToPersist == null) {
      return;
    }
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set(ORGANIZATION_ID_SEARCH_PARAM, orgIdToPersist);
        return next;
      },
      { replace: true },
    );
  }, [orgIdToPersist, setSearchParams]);

  const setSelectedOrganization = useCallback(
    (org: Organization) => {
      // replace: true so flipping orgs doesn't pollute the back-button history.
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set(ORGANIZATION_ID_SEARCH_PARAM, String(org.id));
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return { selectedOrganization, setSelectedOrganization };
}

export const SelectedOrganizationProvider = ctx.Provider;

export function useSelectedOrganization(): SelectedOrganizationContextValue {
  const value = useContext(ctx);
  if (!value) {
    throw new Error('useSelectedOrganization must be used inside SelectedOrganizationProvider');
  }
  return value;
}
